import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Users, Plus, RefreshCw, Key, UserCheck, UserX, Loader2, Check, Shield, User } from 'lucide-react';
import { getUsers, createUser, rotateUserCode, toggleUserActive } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showRotateDialog, setShowRotateDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);

  const [newUser, setNewUser] = useState({ name: '', access_code: '', role: 'staff' });
  const [newCode, setNewCode] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async () => {
    if (!newUser.name.trim() || !newUser.access_code.trim()) {
      toast.error('Name and access code are required');
      return;
    }

    setSaving(true);
    try {
      await createUser(newUser);
      toast.success('User created');
      setShowAddDialog(false);
      setNewUser({ name: '', access_code: '', role: 'staff' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleRotateCode = async () => {
    if (!newCode.trim()) {
      toast.error('New access code is required');
      return;
    }

    setSaving(true);
    try {
      await rotateUserCode(showRotateDialog.id, newCode);
      toast.success('Access code updated');
      setShowRotateDialog(null);
      setNewCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update code');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (userId) => {
    setToggling(userId);
    try {
      const res = await toggleUserActive(userId);
      toast.success(`User ${res.data.active ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle user status');
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-enter" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-heading font-bold">User Management</h1>
          <p className="text-sm text-muted-foreground">{users.length} users</p>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          className="h-10 px-4"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Users List */}
      {users.length === 0 ? (
        <div className="empty-state py-12">
          <Users className="empty-state-icon" />
          <h3 className="empty-state-title">No Users</h3>
          <p className="empty-state-text">Add users to get started</p>
        </div>
      ) : (
        <div 
          className="space-y-2" 
          data-testid="users-list"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {users.map((user) => (
            <div
              key={user.id}
              className={`item-card flex items-center gap-3 ${!user.active ? 'opacity-60' : ''}`}
              data-testid={`user-${user.id}`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                user.role === 'admin' ? 'bg-primary/10' : 'bg-slate-100'
              }`}>
                {user.role === 'admin' ? (
                  <Shield className="w-5 h-5 text-primary" />
                ) : (
                  <User className="w-5 h-5 text-slate-500" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm">{user.name}</p>
                  {user.id === currentUser?.id && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">You</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowRotateDialog(user)}
                  className="btn-icon w-9 h-9"
                  aria-label="Rotate access code"
                  data-testid={`rotate-code-${user.id}`}
                >
                  <Key className="w-4 h-4" />
                </button>
                
                {user.id !== currentUser?.id && (
                  <button
                    onClick={() => handleToggleActive(user.id)}
                    disabled={toggling === user.id}
                    className={`btn-icon w-9 h-9 ${user.active ? 'text-success' : 'text-destructive'}`}
                    aria-label={user.active ? 'Deactivate' : 'Activate'}
                    data-testid={`toggle-user-${user.id}`}
                  >
                    {toggling === user.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : user.active ? (
                      <UserCheck className="w-4 h-4" />
                    ) : (
                      <UserX className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="userName">Name *</Label>
              <Input
                id="userName"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="mt-1"
                placeholder="John Doe"
                data-testid="new-user-name"
              />
            </div>

            <div>
              <Label htmlFor="accessCode">Access Code *</Label>
              <Input
                id="accessCode"
                type="password"
                value={newUser.access_code}
                onChange={(e) => setNewUser({ ...newUser, access_code: e.target.value })}
                className="mt-1"
                placeholder="Enter access code"
                data-testid="new-user-code"
              />
            </div>

            <div>
              <Label htmlFor="role">Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="mt-1" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={handleCreateUser}
              disabled={saving}
              className="w-full btn-primary-action"
              data-testid="confirm-add-user"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  <span>Create User</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate Code Dialog */}
      <Dialog open={!!showRotateDialog} onOpenChange={() => setShowRotateDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rotate Access Code</DialogTitle>
            <DialogDescription>
              Set a new access code for <strong>{showRotateDialog?.name}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <Label htmlFor="newCode">New Access Code</Label>
            <Input
              id="newCode"
              type="password"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="mt-1"
              placeholder="Enter new code"
              data-testid="new-code-input"
            />
          </div>

          <DialogFooter>
            <Button
              onClick={handleRotateCode}
              disabled={saving}
              className="w-full btn-primary-action"
              data-testid="confirm-rotate-code"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Key className="w-5 h-5" />
                  <span>Update Code</span>
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
